import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Entity, stringifyEntityRef } from '@backstage/catalog-model';
import { identityApiRef, useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { Loaded, useInsights } from '../catalog/pipeline/useInsights';
import { allTargetsOf, unresolvedOf } from '../catalog/component/relations';
import type { DeploymentsSnapshot } from '../deployments/snapshot';
import { CiHealthSnapshot, slugOf } from './ciSnapshot';

/** Who is reading, as the sign-in token says. */
export type Reader = {
  userEntityRef: string;
  ownershipRefs: string[];
  /**
   * Whether any of the reader's ownership refs is a group. The token carries
   * one per team the identity provider lists, and none for a colleague whose
   * teams are not set yet (DX-202) — the state most colleagues are in.
   */
  inTeam: boolean;
};

/** A component the reader's team owns, with what the queue needs of it. */
export type OwnedComponent = {
  ref: string;
  name: string;
  namespace: string;
  title: string;
  slug?: string;
  entity: Entity;
};

type HomeSources = {
  reader: Loaded<Reader>;
  owned: Loaded<OwnedComponent[]>;
  /** Per owned component, the refs among its relations the catalog cannot find. */
  unresolved: Loaded<Record<string, string[]>>;
  ci: Loaded<CiHealthSnapshot>;
  deployments: Loaded<DeploymentsSnapshot>;
};

/** Enough for the attention card, the figure and the ownership card's table. */
const OWNED_FIELDS = [
  'kind',
  'metadata.name',
  'metadata.namespace',
  'metadata.title',
  'metadata.annotations',
  'spec.type',
  'spec.lifecycle',
  'relations',
];

const isGroupRef = (ref: string) => ref.toLowerCase().startsWith('group:');

const HomeSourcesContext = createContext<HomeSources | undefined>(undefined);

/**
 * Asks the home page's sources once, for every part of the page that reads them
 * (DX-307): the attention card, the workspace figures, the recently used table
 * and the cards below them.
 *
 * Asked separately, the attention card and CI health could report two
 * different snapshots of one fact — a repository failing in one and passing in
 * the other — with nothing on the page to reconcile them. The pull-request
 * lists have their own provider for the same reason, and the component page
 * does the same with its context.
 *
 * Each source still resolves on its own: one that is slow or fails leaves the
 * others standing (specs/home-page.md AC-2).
 */
export const HomeSourcesProvider = ({ children }: { children: ReactNode }) => {
  const identityApi = useApi(identityApiRef);
  const catalogApi = useApi(catalogApiRef);

  const [reader, setReader] = useState<Loaded<Reader>>({ loading: true });
  const [owned, setOwned] = useState<Loaded<OwnedComponent[]>>({
    loading: true,
  });
  const [unresolved, setUnresolved] = useState<
    Loaded<Record<string, string[]>>
  >({ loading: true });

  const ci = useInsights<CiHealthSnapshot>('/ci-health');
  const deployments = useInsights<DeploymentsSnapshot>('/deployments');

  useEffect(() => {
    let active = true;
    (async () => {
      let refs: string[];
      try {
        const identity = await identityApi.getBackstageIdentity();
        refs = identity.ownershipEntityRefs ?? [];
        if (!active) return;
        setReader({
          loading: false,
          data: {
            userEntityRef: identity.userEntityRef,
            ownershipRefs: refs,
            inTeam: refs.some(isGroupRef),
          },
        });
      } catch (error) {
        if (!active) return;
        setReader({ loading: false, error: error as Error });
        setOwned({ loading: false, error: error as Error });
        setUnresolved({ loading: false, error: error as Error });
        return;
      }

      // No ownership on the identity: nothing is owned, and the catalog is not
      // asked — an empty `relations.ownedBy` filter is an ambiguous question
      // (specs/services-you-own.md AC-6 holds the same for the ownership card).
      if (refs.length === 0) {
        if (!active) return;
        setOwned({ loading: false, data: [] });
        setUnresolved({ loading: false, data: {} });
        return;
      }

      let components: OwnedComponent[];
      try {
        const { items } = await catalogApi.getEntities({
          filter: { kind: 'Component', 'relations.ownedBy': refs },
          fields: OWNED_FIELDS,
        });
        components = items.map(entity => ({
          ref: stringifyEntityRef(entity),
          name: entity.metadata.name,
          namespace: entity.metadata.namespace ?? 'default',
          title: entity.metadata.title ?? entity.metadata.name,
          slug: slugOf(entity),
          entity,
        }));
        if (!active) return;
        setOwned({ loading: false, data: components });
      } catch (error) {
        if (!active) return;
        setOwned({ loading: false, error: error as Error });
        setUnresolved({ loading: false, error: error as Error });
        return;
      }

      // Every ref the reader's components name, asked of the catalog once —
      // the same reading the component page's banner makes for one component
      // (relations.ts), here for all of them in a single request.
      try {
        const asked = [
          ...new Set(components.flatMap(c => allTargetsOf(c.entity))),
        ];
        const found = new Map<string, Entity | undefined>();
        if (asked.length > 0) {
          const { items } = await catalogApi.getEntitiesByRefs({
            entityRefs: asked,
            fields: ['kind', 'metadata.name', 'metadata.namespace'],
          });
          asked.forEach((ref, i) => found.set(ref, items[i] ?? undefined));
        }
        const byComponent: Record<string, string[]> = {};
        for (const c of components) {
          const missing = unresolvedOf(allTargetsOf(c.entity), found);
          if (missing.length > 0) byComponent[c.ref] = missing;
        }
        if (active) setUnresolved({ loading: false, data: byComponent });
      } catch (error) {
        if (active) setUnresolved({ loading: false, error: error as Error });
      }
    })();
    return () => {
      active = false;
    };
  }, [identityApi, catalogApi]);

  const value = useMemo(
    () => ({ reader, owned, unresolved, ci, deployments }),
    [reader, owned, unresolved, ci, deployments],
  );
  return (
    <HomeSourcesContext.Provider value={value}>
      {children}
    </HomeSourcesContext.Provider>
  );
};

/**
 * A card's failure, in the words the card has always said it in — "Failed to
 * load CI health (502 Bad Gateway)" — made once per failure rather than on every
 * render. For the cards that read a shared source rather than asking for it.
 */
export const useCardError = (error: Error | undefined, what: string) =>
  useMemo(
    () => error && new Error(`Failed to load ${what} (${error.message})`),
    [error, what],
  );

/** The home page's sources, from the `HomeSourcesProvider` around them. */
export const useHomeSources = (): HomeSources => {
  const sources = useContext(HomeSourcesContext);
  if (!sources) {
    throw new Error('useHomeSources needs a HomeSourcesProvider');
  }
  return sources;
};
